import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Wallet as WalletIcon, RefreshCw, Lock, Unlock, Plus, Minus,
  Loader2, Gift, ShoppingCart, TrendingDown, ShieldCheck, Undo2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';


const TX_META = {
  ONBOARDING_BONUS: { icon: Gift,         color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
  TOPUP_PURCHASE:   { icon: ShoppingCart, color: 'text-sky-400 border-sky-500/30 bg-sky-500/10' },
  USAGE_HOLD:       { icon: TrendingDown, color: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
  USAGE_SUCCESS:    { icon: ShieldCheck,  color: 'text-rose-400 border-rose-500/30 bg-rose-500/10' },
  USAGE_REFUND:     { icon: Undo2,        color: 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10' },
  ADMIN_ADJUSTMENT: { icon: ShieldCheck,  color: 'text-purple-300 border-purple-500/30 bg-purple-500/10' },
};

function fmt(dt) {
  if (!dt) return '—';
  try { return new Date(dt).toLocaleString('en-IN'); } catch { return dt; }
}


export function AdminWalletDetailPage() {
  const { admin, loading } = useAdminAuth();
  const { orgId } = useParams();
  const [detail, setDetail] = useState(null);
  const [txs, setTxs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [fetching, setFetching] = useState(true);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustSign, setAdjustSign] = useState('+');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setFetching(true);
    try {
      const [d, t] = await Promise.all([
        adminApi.get(`/admin/wallets/${orgId}`),
        adminApi.get(`/admin/wallets/${orgId}/transactions?page=${page}&page_size=${pageSize}`),
      ]);
      setDetail(d.data);
      setTxs(t.data.items || []);
      setTotal(t.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load wallet');
    } finally { setFetching(false); }
  }, [orgId, page, pageSize]);

  useEffect(() => { if (admin) refresh(); }, [admin, refresh]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;
  if (admin.role !== 'superadmin') {
    return <Navigate to="/admin" replace />;
  }

  const wallet = detail?.wallet;
  const org = detail?.organization;
  const owner = detail?.owner;

  const doAdjust = async ({ amount, reason }) => {
    const delta = adjustSign === '+' ? Math.abs(amount) : -Math.abs(amount);
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/wallets/${orgId}/adjust`, { delta, reason });
      setDetail((prev) => (prev ? { ...prev, wallet: r.data.wallet } : prev));
      toast.success(`Adjusted ${delta >= 0 ? '+' : ''}${delta} credits`);
      setAdjustOpen(false);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Adjust failed'); }
    finally { setBusy(false); }
  };

  const toggleLock = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/wallets/${orgId}/lock`, {
        is_locked: !wallet?.is_locked,
      });
      setDetail((prev) => (prev ? { ...prev, wallet: r.data.wallet } : prev));
      toast.success(r.data.wallet.is_locked ? 'Wallet locked' : 'Wallet unlocked');
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Lock toggle failed'); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-wallet-detail-page" className="min-h-screen" style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}>
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin/wallets" data-testid="admin-wallet-back" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <WalletIcon className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold truncate">{org?.name || 'Loading…'}</div>
            <div className="text-xs text-slate-400 font-mono truncate">{orgId}</div>
          </div>
          <Button onClick={refresh} disabled={fetching} variant="outline" size="sm" data-testid="admin-wallet-refresh" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fetching ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-6 space-y-6">
        {fetching && !detail ? (
          <div className="p-10 flex items-center justify-center gap-2 text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-wallet-hero">
              <div className="md:col-span-2 rounded-2xl p-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-emerald-500/20 shadow-xl relative overflow-hidden">
                <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-emerald-500/10 blur-2xl" />
                <div className="text-xs uppercase tracking-widest text-emerald-300/80 mb-2">Available balance</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-white tabular-nums" data-testid="admin-wallet-balance">{Number(wallet?.balance ?? 0).toLocaleString('en-IN')}</span>
                  <span className="text-slate-400">credits</span>
                  {wallet?.is_locked && (
                    <span className="ml-3 text-[10px] uppercase tracking-wider font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40 px-2 py-0.5 rounded flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Locked
                    </span>
                  )}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Lifetime top-ups</div>
                    <div className="font-mono text-emerald-300">+{Number(wallet?.lifetime_topup ?? 0).toLocaleString('en-IN')}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Lifetime usage</div>
                    <div className="font-mono text-rose-300">−{Number(wallet?.lifetime_usage ?? 0).toLocaleString('en-IN')}</div>
                  </div>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  <Button onClick={() => { setAdjustSign('+'); setAdjustOpen(true); }} disabled={busy} size="sm" data-testid="admin-wallet-credit-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Credit
                  </Button>
                  <Button onClick={() => { setAdjustSign('-'); setAdjustOpen(true); }} disabled={busy} size="sm" data-testid="admin-wallet-debit-btn" className="bg-rose-500 hover:bg-rose-400 text-white font-semibold">
                    <Minus className="w-3.5 h-3.5 mr-1.5" /> Debit
                  </Button>
                  <Button onClick={toggleLock} disabled={busy} variant="outline" size="sm" data-testid="admin-wallet-lock-btn" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
                    {wallet?.is_locked ? <><Unlock className="w-3.5 h-3.5 mr-1.5" /> Unlock</> : <><Lock className="w-3.5 h-3.5 mr-1.5" /> Lock</>}
                  </Button>
                </div>
              </div>

              <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-5 text-sm space-y-2" data-testid="admin-wallet-org-card">
                <div className="text-xs uppercase tracking-widest text-slate-500">Organization</div>
                <div className="text-white font-medium truncate">{org?.name}</div>
                <div className="text-xs text-slate-500 font-mono truncate">{org?.id}</div>
                <div className="text-xs mt-2">
                  <span className="text-slate-500">Tier</span>{' '}
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${
                    org?.license_tier === 'pro' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' : 'bg-slate-700/50 text-slate-300 border-slate-600'
                  }`}>{org?.license_tier || 'none'}</span>
                </div>
                {org?.license_code && (
                  <div className="text-xs text-slate-500">License <span className="font-mono text-slate-300">{org.license_code}</span></div>
                )}
                <div className="pt-3 border-t border-slate-800 mt-2">
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Owner</div>
                  <div className="text-xs text-slate-300 truncate">{owner?.name}</div>
                  <div className="text-xs text-slate-400 truncate">{owner?.email}</div>
                  {owner?.gstin && <div className="text-xs text-slate-500 font-mono">{owner.gstin}</div>}
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-slate-900/70 border border-slate-800 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-200">Transaction ledger</div>
                <div className="text-xs text-slate-500">{total.toLocaleString('en-IN')} entries</div>
              </div>
              {txs.length === 0 ? (
                <div className="p-10 text-center text-slate-500 text-sm">No transactions yet.</div>
              ) : (
                <table className="w-full text-sm" data-testid="admin-wallet-tx-table">
                  <thead className="bg-slate-950/60">
                    <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                      <th className="px-4 py-2 text-left w-44">Type</th>
                      <th className="px-4 py-2 text-left w-40">When</th>
                      <th className="px-4 py-2 text-left">Note / Reference</th>
                      <th className="px-4 py-2 text-right w-24">Amount</th>
                      <th className="px-4 py-2 text-right w-24">Balance</th>
                      <th className="px-4 py-2 text-left w-20">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/70">
                    {txs.map((tx) => {
                      const meta = TX_META[tx.type] || TX_META.ADMIN_ADJUSTMENT;
                      const Icon = meta.icon;
                      const pos = tx.amount >= 0;
                      return (
                        <tr key={tx.id} className="text-slate-200 hover:bg-slate-800/30">
                          <td className="px-4 py-2.5">
                            <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${meta.color}`}>
                              <Icon className="w-3 h-3" /> {tx.type.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">{fmt(tx.created_at)}</td>
                          <td className="px-4 py-2.5">
                            <div className="text-slate-300 truncate max-w-md">{tx.notes}</div>
                            {tx.reference_id && <div className="text-[10px] text-slate-500 font-mono truncate">ref: {tx.reference_id}</div>}
                          </td>
                          <td className={`px-4 py-2.5 text-right font-mono tabular-nums text-sm ${pos ? 'text-emerald-300' : 'text-rose-300'}`}>
                            {pos ? '+' : ''}{tx.amount}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-400 text-xs">
                            {tx.balance_after ?? '—'}
                          </td>
                          <td className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-400">
                            {tx.status}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </main>

      <AdjustDialog
        open={adjustOpen}
        sign={adjustSign}
        busy={busy}
        currentBalance={wallet?.balance ?? 0}
        onClose={() => setAdjustOpen(false)}
        onConfirm={doAdjust}
      />
    </div>
  );
}


function AdjustDialog({ open, sign, busy, currentBalance, onClose, onConfirm }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) { setAmount(''); setReason(''); } }, [open]);
  const parsed = parseInt(amount, 10);
  const isCredit = sign === '+';
  const disabled = !parsed || parsed <= 0 || reason.trim().length < 3
    || (!isCredit && parsed > currentBalance);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="admin-wallet-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCredit ? <Plus className="w-4 h-4 text-emerald-400" /> : <Minus className="w-4 h-4 text-rose-400" />}
            {isCredit ? 'Credit' : 'Debit'} wallet
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {isCredit ? 'Add credits' : 'Deduct credits'} — the ledger row will link back to your SuperAdmin id and this reason. Balances can never go below zero.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-300">Amount (credits)</Label>
            <Input
              type="number" min="1" step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="admin-wallet-adjust-amount"
              className="bg-slate-800/50 border-slate-700 text-white font-mono"
              placeholder="e.g. 50"
            />
            {!isCredit && parsed > currentBalance && (
              <div className="text-xs text-rose-400">Cannot debit more than current balance ({currentBalance}).</div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-300">Reason (required · min 3 chars)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="admin-wallet-adjust-reason"
              rows={3}
              className="bg-slate-800/50 border-slate-700 text-white"
              placeholder={isCredit ? 'e.g. Goodwill credits — support ticket #4823' : 'e.g. Reversal of duplicate manual top-up'}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Cancel</Button>
          <Button
            onClick={() => onConfirm({ amount: parsed, reason: reason.trim() })}
            disabled={disabled || busy}
            data-testid="admin-wallet-adjust-confirm"
            className={`font-semibold ${isCredit ? 'bg-emerald-500 hover:bg-emerald-400 text-slate-950' : 'bg-rose-500 hover:bg-rose-400 text-white'}`}
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : (isCredit ? <Plus className="w-4 h-4 mr-1.5" /> : <Minus className="w-4 h-4 mr-1.5" />)}
            Confirm {isCredit ? 'credit' : 'debit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
